/**
 * Module 'diff': a session checkout's changes, served on demand by the machine agent.
 *
 * The hub asks (`GET /api/sessions/:id/diff`), the agent runs read-only git in the session's
 * folder (`--no-optional-locks`, no fetch, bounded output) and answers with parsed files.
 * Nothing runs on a timer: a diff is computed only while someone looks at it.
 *
 * Browser-safe: the parser below is shared by the agent (to build the answer) and by tests.
 */
import type { Id, Ts } from '../index.ts';

/**
 * working  uncommitted changes: tracked files against HEAD, plus untracked files;
 * branch   every commit since the branch left its base (merge-base with the base ref);
 * commit   one commit (`commit` names it; it must be one of the branch's commits).
 */
export type DiffScope = 'working' | 'branch' | 'commit';
export const DIFF_SCOPES: readonly DiffScope[] = ['working', 'branch', 'commit'];

export type DiffLineKind = 'ctx' | 'add' | 'del' | 'note';

export interface DiffLine {
  kind: DiffLineKind;
  /** The line without its +, - or space marker. */
  text: string;
  /** Line number in the old file (null for added lines and notes). */
  old: number | null;
  /** Line number in the new file (null for deleted lines and notes). */
  new: number | null;
}

export interface DiffHunk {
  /** The raw `@@ -a,b +c,d @@` header. */
  header: string;
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  /** The function or section git printed after the header, if any. */
  section: string;
  lines: DiffLine[];
}

export type DiffFileStatus = 'added' | 'deleted' | 'modified' | 'renamed' | 'copied' | 'untracked';

/** Why a file shows less than all of its changes. */
export type DiffTruncation = 'lines' | 'total' | 'large' | null;

export interface DiffFile {
  /** Path in the new tree (the old path for a deleted file). */
  path: string;
  /** Set for renames and copies. */
  oldPath: string | null;
  status: DiffFileStatus;
  binary: boolean;
  additions: number;
  deletions: number;
  hunks: DiffHunk[];
  /** 'lines': this file had more than DIFF_LIMITS.fileLines lines of diff; 'total': the whole diff hit its size cap before this file; 'large': an untracked file too large to show. */
  truncated: DiffTruncation;
  /** e.g. "100644 → 100755" when only the mode changed. */
  modeChange?: string;
}

export interface DiffCommit {
  sha: string;
  short: string;
  subject: string;
  author: string;
  at: Ts;
}

export interface DiffTotals {
  files: number;
  additions: number;
  deletions: number;
}

/** The answer to a diff request. */
export interface DiffResult {
  sessionId: Id;
  scope: DiffScope;
  /** For scope 'commit'. */
  commit?: DiffCommit;
  /** The session's folder and the repository root it is in. */
  cwd: string;
  root: string;
  branch: string | null;
  head: string | null;
  /** What 'branch' compares with: the base ref and the merge-base commit. Null when none was found. */
  base: { ref: string; sha: string } | null;
  /** Commits since the base, newest first (at most DIFF_LIMITS.commits). */
  commits: DiffCommit[];
  commitsOmitted: number;
  files: DiffFile[];
  totals: DiffTotals;
  /** True when anything was left out (a size cap, a file cap). The UI says what. */
  truncated: boolean;
  /** A plain sentence the UI shows above the files, e.g. why there is no base. */
  note?: string;
  at: Ts;
  /** How long git took on the machine, ms. */
  tookMs: number;
}

/** GET /api/sessions/:id/diff?scope=&commit= */
export interface DiffQuery {
  scope: DiffScope;
  commit?: string;
}

/** hub -> agent, ExtMessage { ext: 'diff', type: 'request', id }. */
export interface DiffRequestMessage {
  sessionId: Id;
  scope: DiffScope;
  commit?: string;
  /**
   * The project the commit landed in (QA-54): a landed mission's worktree is removed, so its
   * session no longer places itself in a project; the commit is read from that project's main checkout.
   */
  projectId?: Id;
}

/** agent -> hub, ExtMessage { ext: 'diff', type: 'result', id } (same id). */
export type DiffResultMessage =
  | { ok: true; result: DiffResult }
  | { ok: false; error: { what: string; why: string; next: string; code?: string } };

/** Size caps. A diff is read-only and on demand, and still never unbounded. */
export const DIFF_LIMITS = {
  /** Bytes of patch text read from git; the rest is cut and marked. */
  bytes: 1_500_000,
  /** Diff lines kept per file. */
  fileLines: 3000,
  /** Files listed at most. */
  files: 400,
  /** Commits listed for scope 'branch'. */
  commits: 100,
  /** Untracked files shown with their content at most, and the size of each. */
  untrackedFiles: 50,
  untrackedBytes: 64 * 1024,
  /** git gets this long per command. */
  timeoutMs: 15_000,
} as const;

/** A commit id the agent will pass to git: hex only, so it can never be an option. */
export const COMMIT_SHA_RE = /^[0-9a-f]{7,40}$/i;

export const DIFF_SCOPE_LABEL: Record<DiffScope, string> = {
  working: 'Uncommitted',
  branch: 'Branch',
  commit: 'Commit',
};

export const DIFF_STATUS_LABEL: Record<DiffFileStatus, string> = {
  added: 'Added',
  deleted: 'Deleted',
  modified: 'Modified',
  renamed: 'Renamed',
  copied: 'Copied',
  untracked: 'Untracked',
};

// ---------------------------------------------------------------------------
// Parsing `git diff` output
// ---------------------------------------------------------------------------

/** Undo git's C-style quoting of a path ("a/t\303\244st" or "a/with \"quote\""). */
export function unquoteGitPath(p: string): string {
  if (!(p.length >= 2 && p.startsWith('"') && p.endsWith('"'))) return p;
  const body = p.slice(1, -1);
  // Octal escapes are UTF-8 bytes: collect everything percent-encoded, then decode once.
  let enc = '';
  for (let i = 0; i < body.length; i++) {
    const c = body[i] as string;
    if (c !== '\\') {
      enc += encodeURIComponent(c);
      continue;
    }
    const n = body[i + 1] ?? '';
    if (/[0-7]/.test(n)) {
      const byte = parseInt(body.slice(i + 1, i + 4), 8) & 0xff;
      enc += `%${byte.toString(16).padStart(2, '0')}`;
      i += 3;
      continue;
    }
    const map: Record<string, string> = { n: '\n', t: '\t', r: '\r', '"': '"', '\\': '\\', a: '\x07', b: '\b', f: '\f', v: '\v' };
    enc += encodeURIComponent(map[n] ?? n);
    i += 1;
  }
  try {
    return decodeURIComponent(enc);
  } catch {
    return body;
  }
}

/** Strip the a/ or b/ prefix from a ---/+++ path; null for /dev/null. */
function sidePath(raw: string): string | null {
  let p = raw.replace(/\t.*$/, '');
  p = unquoteGitPath(p);
  if (p === '/dev/null') return null;
  return p.replace(/^[ab]\//, '');
}

/** Paths from `diff --git a/X b/Y` (best effort; ---/+++ and rename lines win when present). */
function headerPaths(rest: string): { a: string; b: string } | null {
  if (rest.startsWith('"')) {
    const m = /^("(?:[^"\\]|\\.)*")\s+("(?:[^"\\]|\\.)*"|\S.*)$/.exec(rest);
    if (m) return { a: sidePath(m[1] as string) ?? '', b: sidePath(m[2] as string) ?? '' };
  }
  // Same path on both sides: "a/P b/P" splits exactly in the middle.
  const n = rest.length;
  if ((n - 3) % 2 === 0) {
    const half = (n - 3) / 2;
    const a = rest.slice(0, half);
    const b = rest.slice(half + 1);
    if (rest[half] === ' ' && a.slice(2) === b.slice(2)) return { a: a.slice(2), b: b.slice(2) };
  }
  const i = rest.lastIndexOf(' b/');
  if (i > 0) return { a: rest.slice(0, i).replace(/^a\//, ''), b: rest.slice(i + 3) };
  return null;
}

const HUNK_RE = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@ ?(.*)$/;

function newFile(): DiffFile {
  return { path: '', oldPath: null, status: 'modified', binary: false, additions: 0, deletions: 0, hunks: [], truncated: null };
}

export interface ParseOptions {
  /** Diff lines kept per file (default DIFF_LIMITS.fileLines). */
  fileLines?: number;
  /** The text was cut short: the last file is marked truncated 'total'. */
  cut?: boolean;
}

/**
 * Parse `git diff` / `git show` patch output (with a/ b/ prefixes) into files and hunks.
 * Tolerant: lines it does not know are skipped. Addition and deletion counts cover the
 * whole file even when its lines are capped.
 */
export function parseUnifiedDiff(patch: string, opts: ParseOptions = {}): DiffFile[] {
  const cap = opts.fileLines ?? DIFF_LIMITS.fileLines;
  const files: DiffFile[] = [];
  let f: DiffFile | null = null;
  let h: DiffHunk | null = null;
  let kept = 0;
  let oldNo = 0;
  let newNo = 0;
  let oldMode = '';
  const lines = patch.split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  const finish = () => {
    if (f && !f.path) f.path = f.oldPath ?? '';
    if (f && f.oldPath === f.path && f.status !== 'renamed' && f.status !== 'copied') f.oldPath = null;
  };
  for (const raw of lines) {
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    if (line.startsWith('diff --git ')) {
      finish();
      f = newFile();
      files.push(f);
      h = null;
      kept = 0;
      oldMode = '';
      const p = headerPaths(line.slice('diff --git '.length));
      if (p) {
        f.oldPath = p.a;
        f.path = p.b;
      }
      continue;
    }
    if (!f) continue;
    if (h) {
      const c = line[0];
      if (c === '+' || c === '-' || c === ' ' || (c === undefined && line === '')) {
        // An empty line inside a hunk is a context line whose space was trimmed by an editor.
        const kind: DiffLineKind = c === '+' ? 'add' : c === '-' ? 'del' : 'ctx';
        if (kind === 'add') f.additions++;
        if (kind === 'del') f.deletions++;
        const entry: DiffLine = { kind, text: line.slice(1), old: kind === 'add' ? null : oldNo, new: kind === 'del' ? null : newNo };
        if (kind !== 'add') oldNo++;
        if (kind !== 'del') newNo++;
        if (kept < cap) {
          h.lines.push(entry);
          kept++;
        } else f.truncated = 'lines';
        continue;
      }
      if (c === '\\') {
        if (kept < cap) h.lines.push({ kind: 'note', text: line.slice(2), old: null, new: null });
        continue;
      }
      if (!line.startsWith('@@')) h = null;
    }
    const m = HUNK_RE.exec(line);
    if (m) {
      h = {
        header: `@@ -${m[1]}${m[2] !== undefined ? `,${m[2]}` : ''} +${m[3]}${m[4] !== undefined ? `,${m[4]}` : ''} @@`,
        oldStart: Number(m[1]),
        oldLines: m[2] === undefined ? 1 : Number(m[2]),
        newStart: Number(m[3]),
        newLines: m[4] === undefined ? 1 : Number(m[4]),
        section: (m[5] ?? '').trim(),
        lines: [],
      };
      oldNo = h.oldStart;
      newNo = h.newStart;
      if (kept < cap) f.hunks.push(h);
      else f.truncated = 'lines';
      continue;
    }
    if (line.startsWith('new file mode')) f.status = 'added';
    else if (line.startsWith('deleted file mode')) f.status = 'deleted';
    else if (line.startsWith('rename from ')) {
      f.status = 'renamed';
      f.oldPath = unquoteGitPath(line.slice('rename from '.length));
    } else if (line.startsWith('rename to ')) f.path = unquoteGitPath(line.slice('rename to '.length));
    else if (line.startsWith('copy from ')) {
      f.status = 'copied';
      f.oldPath = unquoteGitPath(line.slice('copy from '.length));
    } else if (line.startsWith('copy to ')) f.path = unquoteGitPath(line.slice('copy to '.length));
    else if (line.startsWith('old mode ')) oldMode = line.slice('old mode '.length);
    else if (line.startsWith('new mode ')) f.modeChange = `${oldMode} → ${line.slice('new mode '.length)}`;
    else if (line.startsWith('Binary files ') || line === 'GIT binary patch') f.binary = true;
    else if (line.startsWith('--- ')) {
      const p = sidePath(line.slice(4));
      if (p !== null && f.status !== 'renamed' && f.status !== 'copied') f.oldPath = p;
    } else if (line.startsWith('+++ ')) {
      const p = sidePath(line.slice(4));
      if (p !== null) f.path = p;
      else if (f.oldPath) f.path = f.oldPath;
    }
  }
  finish();
  if (opts.cut && files.length) (files[files.length - 1] as DiffFile).truncated = 'total';
  return files;
}

export function diffTotals(files: readonly DiffFile[]): DiffTotals {
  let additions = 0;
  let deletions = 0;
  for (const f of files) {
    additions += f.additions;
    deletions += f.deletions;
  }
  return { files: files.length, additions, deletions };
}

/** One synthetic 'added' file for an untracked text file's content. */
export function untrackedFile(path: string, content: string | null, why: 'large' | 'binary' | null = null): DiffFile {
  const f: DiffFile = { ...newFile(), path, status: 'untracked', binary: why === 'binary', truncated: why === 'large' ? 'large' : null };
  if (content === null) return f;
  const lines = content.split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  const cap = DIFF_LIMITS.fileLines;
  const h: DiffHunk = { header: `@@ -0,0 +1,${lines.length} @@`, oldStart: 0, oldLines: 0, newStart: 1, newLines: lines.length, section: '', lines: [] };
  lines.forEach((t, i) => {
    if (i < cap) h.lines.push({ kind: 'add', text: t.endsWith('\r') ? t.slice(0, -1) : t, old: null, new: i + 1 });
  });
  if (lines.length > cap) f.truncated = 'lines';
  if (!content.endsWith('\n') && lines.length) h.lines.push({ kind: 'note', text: 'No newline at end of file', old: null, new: null });
  f.additions = lines.length;
  f.hunks = lines.length ? [h] : [];
  return f;
}
